/**
 * SchichtTagsModal – Zeigt den gesamten Schichtzyklus als Kalender-Grid.
 * Die Ziel-Schicht (z.B. Frühschicht) ist hervorgehoben und auswählbar.
 * Alle anderen Schichten sind sichtbar (als Kontext) aber nicht klickbar.
 * Genau so wie der Schichtrhythmus-Modal, aber mit Markierfunktion.
 */
import React, { useState, useMemo } from 'react';
import { X, Check } from 'lucide-react';

const WD_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const SCHICHT_COLOR = {
  Früh:  'bg-orange-100 text-orange-800 border-orange-300',
  Spät:  'bg-blue-100 text-blue-800 border-blue-300',
  Nacht: 'bg-purple-100 text-purple-800 border-purple-300',
  Frei:  'bg-teal-50 text-teal-600 border-teal-200',
};
const SCHICHT_COLOR_SELECTED = {
  Früh:  'bg-orange-500 text-white border-orange-600',
  Spät:  'bg-blue-500 text-white border-blue-600',
  Nacht: 'bg-purple-500 text-white border-purple-600',
  Frei:  'bg-teal-500 text-white border-teal-600',
};
const SCHICHT_SHORT = { Früh: 'F', Spät: 'S', Nacht: 'N', Frei: '·' };

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function isoWeek(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const jan4 = new Date(d.getFullYear(), 0, 4);
  return Math.ceil((((d - jan4) / 86400000) + jan4.getDay() + 1) / 7);
}

export default function SchichtTagsModal({ rhythmus, targetSchicht, selectedPositions = [], onSave, onClose }) {
  const [selected, setSelected] = useState(new Set(selectedPositions));

  const displayName = targetSchicht === 'Frei' ? 'Freie Tage' : `${targetSchicht}schicht`;

  // Alle Positionen im Zyklus die dem Ziel-Schichttyp entsprechen
  const targetPositions = useMemo(() =>
    rhythmus.days
      .map((d, i) => ({ schicht: d ?? 'Frei', i }))
      .filter(({ schicht }) => schicht === targetSchicht)
      .map(({ i }) => i),
  [rhythmus, targetSchicht]);

  // Kalender-Grid aus dem Zyklus aufbauen (wie SchichtRhythmusModal)
  const weeks = useMemo(() => {
    const start   = rhythmus.startDate;
    const startWD = (new Date(start + 'T12:00:00').getDay() + 6) % 7; // Mon=0
    // Montag der Startwoche
    const gridStart  = addDays(start, -startWD);
    const weeksCount = Math.ceil((startWD + rhythmus.cycleLength) / 7);

    const result = [];
    for (let w = 0; w < weeksCount; w++) {
      const row = [];
      for (let d = 0; d < 7; d++) {
        const cellDate    = addDays(gridStart, w * 7 + d);
        const cycleOffset = Math.round(
          (new Date(cellDate + 'T12:00:00') - new Date(start + 'T12:00:00')) / 86400000
        );
        const inCycle = cycleOffset >= 0 && cycleOffset < rhythmus.cycleLength;
        const schicht = inCycle ? (rhythmus.days[cycleOffset] ?? 'Frei') : null;
        const dayNum  = new Date(cellDate + 'T12:00:00').getDate();
        row.push({ date: cellDate, pos: cycleOffset, schicht, inCycle, dayNum });
      }
      result.push({ kw: isoWeek(addDays(gridStart, w * 7 + 3)), days: row });
    }
    return result;
  }, [rhythmus]);

  const toggle = (pos) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pos)) next.delete(pos); else next.add(pos);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(targetPositions));
  const clearAll  = () => setSelected(new Set());

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[400] p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[88vh] flex flex-col overflow-hidden"
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-slate-800 text-base">{displayName} auswählen</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Tippe auf hervorgehobene Tage zum Markieren
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick actions */}
        <div className="flex-shrink-0 px-4 py-2 border-b border-slate-100 flex items-center gap-2">
          <button type="button" onClick={selectAll}
            className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs rounded-lg font-medium transition-colors">
            Alle
          </button>
          <button type="button" onClick={clearAll}
            className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs rounded-lg font-medium transition-colors">
            Keine
          </button>
          <span className="text-[10px] text-slate-400 ml-auto">
            {selected.size} / {targetPositions.length} ausgewählt
          </span>
        </div>

        {/* Kalender-Grid – genau wie SchichtRhythmusModal */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {/* Wochentag-Header */}
          <div className="grid grid-cols-8 gap-0.5 mb-1">
            <div className="text-[9px] text-slate-300 text-center">KW</div>
            {WD_SHORT.map(d => (
              <div key={d} className="text-[10px] font-semibold text-slate-500 text-center">{d}</div>
            ))}
          </div>

          {/* Kalender-Zeilen */}
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-8 gap-0.5 mb-0.5">
              <div className="text-[9px] text-slate-300 flex items-center justify-center">
                {week.kw}
              </div>
              {week.days.map(({ date, pos, schicht, inCycle, dayNum }) => {
                if (!inCycle) {
                  return <div key={date} className="rounded border border-transparent aspect-square" />;
                }

                const isTarget   = schicht === targetSchicht;
                const isSelected = isTarget && selected.has(pos);

                return (
                  <button
                    key={date}
                    type="button"
                    disabled={!isTarget}
                    onClick={() => isTarget && toggle(pos)}
                    className={`
                      schicht-day-btn relative rounded-lg border text-center py-1 transition-all
                      ${isSelected
                        ? SCHICHT_COLOR_SELECTED[schicht] + ' ring-2 ring-offset-1 shadow-sm'
                        : SCHICHT_COLOR[schicht] || SCHICHT_COLOR['Frei']
                      }
                      ${isTarget ? 'cursor-pointer hover:opacity-80' : 'cursor-default opacity-35'}
                    `}
                  >
                    <div className="text-[9px] leading-none opacity-60">{dayNum}</div>
                    <div className="text-[11px] font-bold leading-none mt-0.5">
                      {isSelected
                        ? <Check className="w-3 h-3 mx-auto" />
                        : SCHICHT_SHORT[schicht]
                      }
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-slate-100 px-4 py-3">
          <button type="button"
            onClick={() => onSave([...selected])}
            className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
              selected.size > 0
                ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-sm'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
            disabled={selected.size === 0}>
            Auswahl speichern ({selected.size} / {targetPositions.length})
          </button>
        </div>
      </div>
    </div>
  );
}
